import httpRequestHandler, { CancelToken, isCancel } from 'src/utils/httpRequestHandler';
import claimsHttpRequestHandler from 'src/utils/ClaimsHttpRequestHandler';
import convertModelToQueryString from 'src/utils/convertModelToQueryString';
import CACHE_TIME from 'src/constants/cacheTime';
import cacheService from './cacheService';

class ClaimService {
  /** @typedef {import('../services/cacheService').CacheService} CacheService */

  /** @type {CacheService} */
  #cacheService;

  #cancelTokens = [];

  /**
   * @param {CacheService} cacheService
   */
  constructor(cacheService) {
    this.#cacheService = cacheService;
  }

  setAxiosInterceptors = ({ onLogout }) => {
    httpRequestHandler.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          this.setSession(null);

          if (onLogout) {
            onLogout();
          }
        }

        return Promise.reject(error);
      }
    );
    claimsHttpRequestHandler.interceptors.response.use(
      response => response,
      error => {
        if (error.response && error.response.status === 401) {
          this.setSession(null);

          if (onLogout) {
            onLogout();
          }
        }
        return Promise.reject(error);
      }
    );
  };

  setSession = accessToken => {
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      httpRequestHandler.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      claimsHttpRequestHandler.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    } else {
      localStorage.removeItem('accessToken');
      delete httpRequestHandler.defaults.headers.common.Authorization;
      delete claimsHttpRequestHandler.defaults.headers.common.Authorization;
    }
  };

  getAccessToken = () => localStorage.getItem('accessToken');

  listOfCountryStates = async () => {
    const key = 'country-states';
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      const { data } = await claimsHttpRequestHandler.get('data-list/states');
      response = data;
      this.#cacheService.saveToCache(key, response, CACHE_TIME.ONE_DAY);
    }
    return response;
  };

  getDamageReasons = async (coverageIdentifier) => {
    const key = { damageReasonsByCoverageIdentifier: coverageIdentifier };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      const { data } = await claimsHttpRequestHandler.get(
        `/data-list/damageReasonsByCoverageIdentifier/${coverageIdentifier}`
      );
      response = data;
      this.#cacheService.saveToCache(key, response, CACHE_TIME.THIRTY_MIN);
    }
    return response;
  };

  updateClaim = async claim => {
    const { data } = await httpRequestHandler.put('/claims/all', claim);
    return data;
  };

  getClaims = async ({
    userId,
    dealerCode,
    memberId,
    initialClaim
  }) => {
    const { data } = await httpRequestHandler.post('/claims/get-claims', {
      userId,
      dealerCode,
      memberId,
      initialClaim
    },
      {
        cancelToken: new CancelToken((c) => {
          this.#cancelTokens.push(c);
        })
      });
    return data;
  };


  getVendorClaims = async ({
    dealerCode,
    trackingNumber

  }) => {
    const { data } = await httpRequestHandler.post('/claims/get-glass-claims', {
      dealerCode,
      trackingNumber
    },
      {
        cancelToken: new CancelToken((c) => {
          this.#cancelTokens.push(c);
        })
      });
    return data;
  };

  /**
   *
   * @param {object} claimData
   * @returns
   */
  makeClaim = async claimData => {
    const { data } = await httpRequestHandler.post('/claims/create', claimData);
    return data;
  };


  makeVendorClaim = async claimData => {
    const { data } = await httpRequestHandler.post('/claims/create-vendor', claimData);
    return data;
    //for UI testing without submitting actual claim
    // return {
    //   "claim": {
    //     "vendorClaimId": 78,
    //     "claimSource": "VENDOR"
    //   }
    // };
  };


  getClaim = async (claimId) => {
    const { data } = await httpRequestHandler.get(
      `/claims/detail?claimId=${claimId}`
    );
    return data;
  };

  updateClaimStatus = async (claimId, disposition, userEmail, userName, userId) => {
    const { data } = await httpRequestHandler.post(
      `/claims/update-status?claimId=${claimId}&disposition=${disposition}`,
      {
        userEmail,
        userName,
        userId
      }
    );
    return data;
  };

  addClaimNote = async (claimId, claimNote, isProcessorNote, createdBy, userId, userName) => {
    const { data } = await httpRequestHandler.post(
      `/claims/add-claim-note`,
      {
        claimId,
        claimNote,
        isProcessorNote,
        createdBy,
        userId,
        userName
      }
    );
    return data;
  };


  updateClaimNote = async (claimId, claimNote, claimNoteId, isProcessorNote, createdBy, userId, userName) => {
    const data = await httpRequestHandler.post(
      `/claims/update-claim-note`,
      {
        claimId,
        claimNote,
        claimNoteId,
        isProcessorNote,
        createdBy,
        userId,
        userName
      }
    );
    return data;
  };

  deleteClaimNote = async (claimId, claimNoteId, createdBy, userId, userName) => {
    const data = httpRequestHandler.post(
      `/claims/delete-claim-note`,
      {
        claimId,
        claimNoteId,
        createdBy,
        userId,
        userName
      }
    );
    return data;
  };

  getVendorClaim = async (claimId) => {
    const { data } = await httpRequestHandler.get(
      `/claims/vendor-detail?claimId=${claimId}`
    );
    return data;
  };
  getBulkVendorClaim = async (claimIds) => {
    const { data } = await httpRequestHandler.post(
      `/claims/vendor-claim-report-list`,
      { claimIds: claimIds }
    );
    return data;
  };

  getEcpCategories = async () => {
    const key = 'ecp-categories';
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      const { data } = await claimsHttpRequestHandler.get('/data-list/ecpCategories');
      response = data;
      this.#cacheService.saveToCache(key, response, CACHE_TIME.ONE_DAY);
    }
    return response;
  };

  getVehicleMakes = async () => {
    const key = 'vehicle-makes';
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      const { data } = await httpRequestHandler.get('/list/vehicle-makes');
      response = data;
      this.#cacheService.saveToCache(key, response, CACHE_TIME.ONE_DAY);
    }
    return response;
  }

  getPartDetail = async ({ make, partNumber, dateOfLoss, policyMasterIdx }) => {
    const response = await httpRequestHandler.get(
      `/claims/vehicle-sku-detail?vehicleMake=${make}&partNumber=${partNumber}&dateOfLoss=${dateOfLoss}&policyMasterIdx=${policyMasterIdx}`
    );
    return response;
  };

  getTirePartDetails = async (partNumber, partNumberType, dealerGroup) => {
    const response = await httpRequestHandler.get(
      `/claims/tire-sku-lookup?partNumber=${partNumber}&partNumberType=${partNumberType}&dealerGroup=${dealerGroup}`
    );
    return response;
  };

  getTirePrice = async ({
    brand,
    model,
    speed,
    size,
    loadCapacity,
    policyMasterIdx,
    dateOfLoss,
    dealerGroup,
  }) => {
    const response = await httpRequestHandler.get(
      `/claims/tire-price-lookup/?brand=${brand}&model=${model}&speed=${speed}&size=${size}&loadCapacity=${loadCapacity}&policyMasterIdx=${policyMasterIdx}&dateOfLoss=${dateOfLoss}&dealerGroup=${dealerGroup}`
    );
    return response;
  };

  getCVQList = async (planCoverageId, programId) => {
    const key = { planCoverageId, programId };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(
        `claims/cvq-list?planCoverageId=${planCoverageId}&programId=${programId}`
      );
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  getAttributeListByAttributeGroup = async (attributeGroupName) => {
    const key = { attributeGroupName };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(
        `claims/attribute-list?attributeGroupName=${attributeGroupName}`
      );
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  getClaimDocumentMetaData = async docRequest => {
    const qs = convertModelToQueryString(docRequest);

    const response = await httpRequestHandler.get(`/claims/document?${qs}`);
    return response.data.documents;
  };

  getTireBrands = async (dealerGroup) => {
    const key = 'tire-brands';
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(`list/tire-brand?dealerGroup=${dealerGroup}`);
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  getTireModels = async (brand, dealerGroup) => {
    const key = brand;
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(
        `list/tire-model?tireBrand=${brand}&dealerGroup=${dealerGroup}`
      );
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  getTireSizes = async (brand, model, dealerGroup) => {
    const key = { brand, model };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(
        `list/tire-sizes/${brand}?tireModel=${model}&dealerGroup=${dealerGroup}`
      );
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  getTireSpeeds = async ({ brand, model, size, dealerGroup }) => {
    const key = { brand, model, size };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(
        `list/tire-speed/${brand}?tireModel=${model}&tireSize=${size}&dealerGroup=${dealerGroup}`
      );
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  getTireLoadRatings = async ({ brand, model, size, speed, dealerGroup }) => {
    const key = { brand, model, size, speed };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(
        `list/tire-load-capacity/${brand}?tireModel=${model}&tireSize=${size}&tireSpeed=${speed}&dealerGroup=${dealerGroup}`
      );
      this.#cacheService.saveToCache(key, response);
    }
    return response;
  };

  /**
   * Fetches the tire size, speed, and load-capacity dropdown options in a
   * single request. Replaces the three sequential cascade calls
   * (`getTireSizes` → `getTireSpeeds` → `getTireLoadRatings`) with one
   * aggregated round-trip. The response is cached per brand/model/size/speed
   * combination so repeated lookups (e.g. after a SKU auto-fill) are served
   * from cache.
   *
   * @param {{ brand: string, model: string, size?: string, speed?: string, dealerGroup: string }} params
   * @returns {Promise<{ data: { sizes: any[], speeds: any[], loads: any[] } }>}
   */
  getTireOptions = async ({ brand, model, size, speed, dealerGroup }) => {
    // cacheService serializes keys with JSON.stringify; this object literal uses
    // a fixed property order and primitive values, so the serialized key is
    // deterministic and consistent between reads and writes.
    const key = { tireOptions: true, brand, model, size, speed, dealerGroup };
    let response = this.#cacheService.getFromCache(key);
    if (!response) {
      response = await httpRequestHandler.get(`list/tire-options/${encodeURIComponent(brand)}`, {
        params: {
          tireModel: model,
          tireSize: size,
          tireSpeed: speed,
          dealerGroup
        }
      });
      this.#cacheService.saveToCache(key, response, CACHE_TIME.THIRTY_MIN);
    }
    return response;
  };

  cancelRequest = () => {
    for (const token of this.#cancelTokens) {
      token();
    }
    this.#cancelTokens = [];
  }

  /**
   * @param {import('axios').AxiosError} error 
   * @returns {boolean}
   */
  isCancelError = (error) => {
    return isCancel(error);
  }

  lookupPurchaseOrderandDealerCode = async (dealerCode, trackingNumber) => {
    const { data } = await httpRequestHandler.get('/policies/lookup-purchase-order', {
      params: {
        dealerCode,
        trackingNumber
      }
    });
    return data;
  }
}

const claimService = new ClaimService(cacheService);
claimService.setSession(claimService.getAccessToken());

export default claimService;
